import React, { useEffect, useRef, useState } from 'react';
import { addCardFact } from '../../api/cards';
import { refineFix } from '../../api/reviews';
import { splitMarkdownLinks } from '../../services/markdownLinks';
import { PartsText } from './TriageParts';

// The active finding: its fix in the sentence it sits in, the explanation
// and source, and the context actions (Edit, Refine, Copy, Save as fact).

// Magic-wand glyph for Refine — primeicons 4 has no wand/sparkles icon.
export function WandIcon() {
    return (
        <svg
            className="crp-wand-icon"
            width="13"
            height="13"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            aria-hidden="true"
        >
            <path d="M2 14 L9.5 6.5" />
            <path d="M11.5 2v3M10 3.5h3" />
            <path d="M13.5 8.5v2.5M12.25 9.75h2.5" />
        </svg>
    );
}

// Model text with its markdown links (web-search citations) as links.
function LinkedText({ text }) {
    return splitMarkdownLinks(text || '').map((seg, i) => (seg.href ? (
        <a key={i} href={seg.href} target="_blank" rel="noopener noreferrer">{seg.text}</a>
    ) : (
        <React.Fragment key={i}>{seg.text}</React.Fragment>
    )));
}

function sourceHost(url) {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
}

const capitalize = text => (text ? text[0].toUpperCase() + text.slice(1) : '');

const focusAtEnd = (el) => {
    if (!el || document.activeElement === el) return;
    el.focus();
    if (el.setSelectionRange) el.setSelectionRange(el.value.length, el.value.length);
};

function EditBox({ initialText, error, onDone, onCancel }) {
    const [text, setText] = useState(initialText);
    return (
        <>
            <textarea
                ref={focusAtEnd}
                className="crp-edit"
                value={text}
                rows={3}
                onChange={e => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') onCancel();
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onDone(text);
                }}
            />
            {error && <p className="crp-edit-error">{error}</p>}
            <div className="crp-edit-actions">
                <button type="button" className="hk-btn hk-btn-sm crp-mini-btn" onClick={onCancel}>Cancel</button>
                <button type="button" className="hk-btn hk-btn-sm crp-mini-btn" onClick={() => onDone(text)}>Done</button>
            </div>
        </>
    );
}

// Inline Refine: an instruction rewrites the current fix on the Fix refiner
// task's model; each round chains on the previous result.
function RefineRow({ finding, token, currentFix, onRefined, onClose }) {
    const [instruction, setInstruction] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const generate = async () => {
        const text = instruction.trim();
        if (!text || busy) return;
        setBusy(true);
        setError('');
        try {
            onRefined(await refineFix(token, finding.runId, finding.id, text, currentFix));
        } catch (err) {
            setError(err.message || 'Refine failed');
            setBusy(false);
        }
    };

    return (
        <>
            <div className="crp-refine">
                <WandIcon />
                <input
                    ref={focusAtEnd}
                    value={instruction}
                    placeholder="How should the fix change?"
                    disabled={busy}
                    onChange={e => setInstruction(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); generate(); }
                        if (e.key === 'Escape') onClose();
                    }}
                />
                {busy ? (
                    <i className="pi pi-spin pi-spinner crp-refine-spinner" />
                ) : (
                    <button
                        type="button"
                        className="hk-btn hk-btn-sm crp-mini-btn"
                        disabled={!instruction.trim()}
                        onClick={generate}
                    >
                        Generate
                    </button>
                )}
            </div>
            {error && <p className="crp-edit-error">{error}</p>}
        </>
    );
}

// The card's existing "save as fact" form, prefilled from the finding.
function FactForm({ finding, cardId, token, onFactsChange, onSaved, onClose }) {
    const [text, setText] = useState(finding.suggestion || finding.claim_text || '');
    const [sourceUrl, setSourceUrl] = useState(finding.source_url || '');
    const [sourceTitle, setSourceTitle] = useState('');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const facts = await addCardFact(token, cardId, {
                text, source_url: sourceUrl, source_title: sourceTitle, from_review_id: finding.runId,
            });
            if (onFactsChange) onFactsChange(facts);
            onSaved();
        } catch {
            setSaving(false); // keep the form open on error
        }
    };

    return (
        <div className="crp-fact-form">
            <label className="crp-fact-label">Fact text</label>
            <textarea className="crp-fact-input" rows={3} value={text} onChange={e => setText(e.target.value)} />
            <label className="crp-fact-label">Source URL</label>
            <input className="crp-fact-input" value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} />
            <label className="crp-fact-label">Source title</label>
            <input
                className="crp-fact-input"
                value={sourceTitle}
                placeholder="e.g. Wikipedia, Official Report..."
                onChange={e => setSourceTitle(e.target.value)}
            />
            <div className="crp-edit-actions">
                <button type="button" className="hk-btn hk-btn-sm crp-mini-btn" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="button" className="hk-btn hk-btn-sm hk-btn-primary crp-mini-btn" onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save fact'}
                </button>
            </div>
        </div>
    );
}

export default function ActiveFinding({
    finding, color, parts, context, tag, canEdit, canRefine, copied, currentFix,
    editing, editText, editError, onEdit, onEditDone, onEditCancel,
    refineOpen, onRefine, onRefined,
    onCopy, cardId, token, onFactsChange, onFactSaved,
}) {
    const f = finding;
    const addition = f.category === 'suggestion';
    const [factOpen, setFactOpen] = useState(false);
    const cardRef = useRef(null);

    // Keep the card in view when keyboard triage moves to it.
    useEffect(() => {
        cardRef.current?.scrollIntoView?.({ block: 'nearest' });
    }, [f.key]);

    return (
        <div className="crp-card" ref={cardRef}>
            <div className="crp-card-head">
                <span className="crp-dot" style={{ background: color }} />
                <span className="crp-card-tags">
                    <span className="crp-card-subtype" style={{ color }}>{f.subtype}</span>
                    {f.kind === 'review' && f.confidence && (
                        <span className="crp-card-meta"> · {capitalize(f.confidence)} confidence</span>
                    )}
                    {tag && <span className="crp-card-meta"> · {tag}</span>}
                </span>
                <span className="crp-card-actions">
                    {canEdit && (
                        <button type="button" className="crp-icon-btn" title="Edit fix (E)" onClick={onEdit}>
                            <i className="pi pi-pencil" />
                        </button>
                    )}
                    {canRefine && (
                        <button type="button" className="crp-icon-btn" title="Refine with AI (R)" onClick={onRefine}>
                            <WandIcon />
                        </button>
                    )}
                    <button type="button" className="crp-icon-btn" title={copied ? 'Copied' : 'Copy fix (C)'} onClick={onCopy}>
                        <i className={`pi ${copied ? 'pi-check' : 'pi-copy'}`} />
                    </button>
                    {f.category === 'factual' && (
                        <button type="button" className="crp-icon-btn" title="Save as fact" onClick={() => setFactOpen(open => !open)}>
                            <i className="pi pi-bookmark" />
                        </button>
                    )}
                </span>
            </div>

            {editing ? (
                <EditBox initialText={editText} error={editError} onDone={onEditDone} onCancel={onEditCancel} />
            ) : (
                <div
                    className={`crp-fix${canEdit ? '' : ' crp-fix-static'}`}
                    title={canEdit ? 'Click to edit' : undefined}
                    onClick={canEdit ? onEdit : undefined}
                >
                    {context.pre && <span className="crp-ctx">{context.pre}</span>}
                    <PartsText parts={parts} addition={addition} />
                    {context.post && <span className="crp-ctx">{context.post}</span>}
                </div>
            )}

            {f.explanation && <div className="crp-why"><LinkedText text={f.explanation} /></div>}
            {f.source_url && f.category !== 'style' && f.category !== 'proofread' && (
                <a className="crp-source" href={f.source_url} target="_blank" rel="noopener noreferrer">
                    {sourceHost(f.source_url)} ↗
                </a>
            )}

            {refineOpen && (
                <RefineRow
                    key={currentFix}
                    finding={f}
                    token={token}
                    currentFix={currentFix}
                    onRefined={onRefined}
                    onClose={onRefine}
                />
            )}

            {factOpen && (
                <FactForm
                    finding={f}
                    cardId={cardId}
                    token={token}
                    onFactsChange={onFactsChange}
                    onSaved={() => { setFactOpen(false); onFactSaved(); }}
                    onClose={() => setFactOpen(false)}
                />
            )}
        </div>
    );
}
